<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-7xl rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div class="flex items-center gap-4">
          <div class="p-2 bg-brand-600 rounded-xl shadow-lg shadow-brand-500/20">
            <Wallet class="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Proceso de Desembolso</h2>
            <p class="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">EXP: {{ loanData?.code || 'Cargando...' }} • Cajero/Autorizador: {{ approvingUser }}</p>
          </div>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full text-slate-400 hover:text-red-500 transition-colors">
          <X class="w-6 h-6" />
        </button>
      </div>

      <div class="flex overflow-x bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-brand-600 text-brand-600 bg-white dark:bg-slate-900' : 'border-transparent text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300']">
          {{ tab.name }}
        </button>
      </div>

      <div v-if="isLoadingDossier" class="flex-1 flex flex-col items-center justify-center p-20 bg-white dark:bg-slate-900">
        <Loader2 class="w-10 h-10 animate-spin text-brand-600 mb-4" />
        <p class="text-xs font-bold text-slate-400 uppercase tracking-widest">Sincronizando Expediente Único...</p>
      </div>

      <div v-else-if="dossier" class="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/30 dark:bg-slate-950/20">
        <form id="disbursementForm" @submit.prevent="handleSubmit" class="space-y-6">

          <div v-show="activeTab === 'customer'" class="space-y-6 animate-fade-in">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div class="lg:col-span-3 space-y-4">
                <div :class="sectionCardClasses">
                  <label :class="labelClasses" class="text-center w-full mb-2">Foto de Perfil</label>
                  <div class="w-32 h-32 rounded-2xl bg-slate-200 dark:bg-slate-700 mx-auto overflow-hidden border-2 border-white dark:border-slate-800 shadow-sm">
                    <img v-if="dossier.customer.photo_url" :src="dossier.customer.photo_url" class="w-full h-full object-cover" />
                    <User v-else class="w-full h-full p-6 text-slate-400" />
                  </div>
                  <div class="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                    <label :class="labelClasses" class="text-center w-full mb-2">Firma del Titular</label>
                    <div class="bg-white rounded-xl p-2 border border-slate-200">
                      <img v-if="dossier.customer.signature_base64" :src="dossier.customer.signature_base64" class="max-h-16 mx-auto dark:invert-0" />
                    </div>
                  </div>
                </div>
                <div :class="sectionCardClasses" class="space-y-3">
                  <label :class="labelClasses">Documentos DUI/ID</label>
                  <div class="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border dark:border-slate-700">
                    <img v-if="dossier.customer.document_front_url" :src="dossier.customer.document_front_url" class="w-full h-full object-cover" />
                    <div v-else class="w-full h-full flex flex-col items-center justify-center text-[8px] text-slate-400 font-bold uppercase"><CreditCard class="w-4 h-4 mb-1" /> Frontal</div>
                  </div>
                  <div class="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden border dark:border-slate-700">
                    <img v-if="dossier.customer.document_back_url" :src="dossier.customer.document_back_url" class="w-full h-full object-cover" />
                    <div v-else class="w-full h-full flex flex-col items-center justify-center text-[8px] text-slate-400 font-bold uppercase"><CreditCard class="w-4 h-4 mb-1" /> Reverso</div>
                  </div>
                </div>
              </div>

              <div class="lg:col-span-9 space-y-6">
                <div :class="sectionCardClasses">
                  <h3 :class="sectionTitleClasses">Datos Maestros del Cliente</h3>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><label :class="labelClasses">Nombre</label><p :class="dataClasses">{{ dossier.customer.full_name }}</p></div>
                    <div><label :class="labelClasses">Identificación</label><p :class="dataClasses" class="font-mono">{{ dossier.customer.identity_document }}</p></div>
                    <div><label :class="labelClasses">Nacimiento</label><p :class="dataClasses">{{ dossier.customer.birth_date }} ({{ calculateAge(dossier.customer.birth_date) }} años)</p></div>
                    <div><label :class="labelClasses">Género / Civil</label><p :class="dataClasses">{{ dossier.customer.gender === 'M' ? 'Masc.' : 'Fem.' }} • {{ dossier.customer.marital_status }}</p></div>
                    <div><label :class="labelClasses">Profesión</label><p :class="dataClasses">{{ dossier.customer.profession }}</p></div>
                    <div><label :class="labelClasses">Cargas</label><p :class="dataClasses">{{ dossier.customer.dependents_count }} Depend. / {{ dossier.customer.children_count }} Hijos</p></div>
                  </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div :class="sectionCardClasses">
                    <h3 :class="sectionTitleClasses">Vivienda y Localización</h3>
                    <div class="space-y-4">
                      <div><label :class="labelClasses">Dirección</label><p :class="dataClasses" class="text-xs">{{ dossier.customer.exact_address || dossier.customer.community }}</p></div>
                      <div class="grid grid-cols-2 gap-2">
                        <div><label :class="labelClasses">Tipo Vivienda</label><p :class="dataClasses">{{ dossier.customer.housing_type }}</p></div>
                        <div><label :class="labelClasses">Estabilidad</label><p :class="dataClasses">{{ dossier.customer.years_living_there }} años</p></div>
                      </div>
                    </div>
                  </div>
                  <div :class="sectionCardClasses">
                    <h3 :class="sectionTitleClasses">Perfil Laboral</h3>
                    <div class="grid grid-cols-2 gap-4 text-slate-900 dark:text-slate-100">
                      <div><label :class="labelClasses">Empresa</label><p :class="dataClasses">{{ dossier.customer.company_name }}</p></div>
                      <div><label :class="labelClasses">Cargo</label><p :class="dataClasses">{{ dossier.customer.job_position }}</p></div>
                      <div><label :class="labelClasses">Jefe Directo</label><p :class="dataClasses">{{ dossier.customer.immediate_boss }}</p></div>
                      <div><label :class="labelClasses">Antigüedad</label><p :class="dataClasses">{{ dossier.customer.years_working }} años</p></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'credit'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses" class="border-t-4 border-brand-600">
              <h3 :class="sectionTitleClasses">Detalles del Préstamo Autorizado</h3>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div><label :class="labelClasses">Producto</label><p class="text-sm font-bold text-brand-600  dark:text-slate-100">{{ dossier.product.name }}</p></div>
                <div><label :class="labelClasses">Monto</label><p class="text-xl font-black font-mono dark:text-white">${{ formatNum(dossier.loan.amount) }}</p></div>
                <div><label :class="labelClasses">Tasa / Interés</label><p class="text-sm font-bold dark:text-white">{{ dossier.loan.rate }}% ({{ dossier.product.interest_type }})</p></div>
                <div><label :class="labelClasses">Plazo</label><p class="text-sm font-bold dark:text-white">{{ dossier.loan.term }} Meses</p></div>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="bg-slate-900 rounded-3xl p-8 text-white flex justify-between items-center shadow-xl">
                <div>
                  <p class="text-[10px] font-black uppercase text-slate-400">Cuota Mensual</p>
                  <p class="text-4xl font-black font-mono text-brand-400">${{ formatNum(dossier.loan.estimated_quota) }}</p>
                </div>
                <BadgeDollarSign class="w-12 h-12 opacity-20" />
              </div>
              <div :class="sectionCardClasses" class="flex items-center justify-between dark:bg-slate-800">
                <div>
                  <p class="text-[10px] font-black uppercase text-slate-400 dark:text-slate-500">Índice de Cobertura (DSCR)</p>
                  <p :class="['text-4xl font-black font-mono', capacityColor.text]">{{ calculateDSCR() }}x</p>
                </div>
                <Activity class="w-10 h-10 opacity-20 dark:text-white" />
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'payments'" class="animate-fade-in">
             <div :class="sectionCardClasses" class="p-0 overflow-hidden border-none shadow-xl bg-white dark:bg-slate-800">
                <table class="w-full text-sm text-left">
                  <thead class="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-black bg-slate-100 dark:bg-slate-950 border-b dark:border-slate-700">
                    <tr><th class="px-6 py-4">Nº</th><th class="px-6 py-4">Vencimiento</th><th class="px-6 py-4 text-right">Capital</th><th class="px-6 py-4 text-right">Interés</th><th class="px-6 py-4 text-right font-bold text-brand-600">Total</th></tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
                    <tr v-for="p in dossier.payment_plans" :key="p.installment_number" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:text-slate-200">
                      <td class="px-6 py-3 font-bold">{{ p.installment_number }}</td>
                      <td class="px-6 py-3">{{ p.due_date }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.capital) }}</td>
                      <td class="px-6 py-3 text-right font-mono">${{ formatNum(p.interest) }}</td>
                      <td class="px-6 py-3 text-right font-mono font-black text-brand-600">${{ formatNum(p.total_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
             </div>
          </div>

          <div v-show="activeTab === 'balance'" class="space-y-6 animate-fade-in">
            <div :class="sectionCardClasses">
              <h3 :class="sectionTitleClasses">Evaluación de la Actividad Comercial</h3>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div class="md:col-span-2"><label :class="labelClasses">Nombre del Negocio</label><p :class="dataClasses">{{ dossier.evaluation.business_name }}</p></div>
                <div><label :class="labelClasses">Estabilidad</label><p :class="dataClasses">{{ getGlobalText(dossier.evaluation.business_stability, 9) }}</p></div>
                <div><label :class="labelClasses">Tipo/Giro</label><p :class="dataClasses">{{ getGlobalText(dossier.evaluation.business_type, 8) }}</p></div>
              </div>
              <div class="p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl border dark:border-slate-700 mb-6">
                <label :class="labelClasses">Descripción de la Actividad</label>
                <p class="text-xs font-medium dark:text-slate-300 leading-relaxed">{{ dossier.evaluation.business_description }}</p>
              </div>

              <!-- VISOR DE FOTOS DEL NEGOCIO -->
              <div class="mb-6 grid grid-cols-2 gap-4">
                <div v-if="dossier.evaluation.photo_url_1" class="aspect-video rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                   <img :src="dossier.evaluation.photo_url_1" class="w-full h-full object-cover hover:scale-105 transition-transform" />
                </div>
                <div v-if="dossier.evaluation.photo_url_2" class="aspect-video rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                   <img :src="dossier.evaluation.photo_url_2" class="w-full h-full object-cover hover:scale-105 transition-transform" />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div class="space-y-4">
                  <label :class="labelClasses">Ventas Auditadas (Semana Tipo)</label>
                  <div class="grid grid-cols-7 gap-1">
                    <div v-for="day in daysMap" :key="day.key" class="text-center p-2 bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-xl">
                      <p class="text-[8px] font-black text-slate-400 uppercase">{{ day.label }}</p>
                      <p class="text-[10px] font-mono font-bold dark:text-white">${{ formatNum(dossier.evaluation['inc_'+day.key]) }}</p>
                      <div class="mt-1 text-[7px] font-black uppercase p-0.5 rounded" :class="getDayStatusClass(day.labelFull, dossier.evaluation)">{{ getDayShortLabel(day.labelFull, dossier.evaluation) }}</div>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800">
                      <label :class="labelClasses" class="text-emerald-700">Ingreso Semanal</label>
                      <p class="text-lg font-mono font-black text-emerald-600">${{ formatNum(dossier.evaluation.total_weekly_income) }}</p>
                    </div>
                    <div class="p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800">
                      <label :class="labelClasses" class="text-emerald-700">Ingreso Mensual</label>
                      <p class="text-lg font-mono font-black text-emerald-600">${{ formatNum(dossier.evaluation.total_monthly_income) }}</p>
                    </div>
                  </div>
                </div>
                <div class="space-y-4">
                  <label :class="labelClasses">Egresos y Gastos Operativos</label>
                  <div class="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                    <div v-for="(exp, i) in parseJSON(dossier.evaluation.business_expenses_json)" :key="i"
                      class="flex justify-between p-2.5 bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-xl">
                      <span class="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase">{{ exp.type }}: {{ exp.description }}</span>
                      <span class="text-sm font-mono font-bold text-red-500">-${{ formatNum(exp.amount) }}</span>
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-4 mt-2">
                    <div class="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-800">
                      <label :class="labelClasses" class="text-red-700">Costo de Venta</label>
                      <p class="text-sm font-mono font-black text-red-600">-${{ formatNum(dossier.evaluation.estimated_monthly_cost) }}</p>
                    </div>
                    <div class="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-800">
                      <label :class="labelClasses" class="text-red-700">Deuda Externa</label>
                      <p class="text-sm font-mono font-black text-red-600">-${{ formatNum(dossier.evaluation.monthly_debt_payment) }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border dark:border-slate-700">
                <label :class="labelClasses">Observaciones del Analista de Campo</label>
                <p class="text-xs font-medium dark:text-slate-400 italic">"{{ dossier.evaluation.observations || 'Sin observaciones.' }}"</p>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'collaterals'" class="animate-fade-in grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-800 dark:text-slate-200">
            <div v-for="col in dossier.collaterals" :key="col.id" :class="sectionCardClasses" class="border-l-8 border-brand-600 shadow-lg dark:bg-slate-800 dark:border-slate-700">
               <div class="flex justify-between items-start mb-6">
                <div>
                  <span class="px-2 py-0.5 bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 text-[9px] font-black uppercase rounded">{{ col.collateral_type }}</span>
                  <h4 class="text-sm font-black mt-2 uppercase tracking-tight">{{ col.pledge_type || col.mortgage_type || 'Garantía Registrada' }}</h4>
                  <p class="text-[9px] font-bold text-slate-400 mt-1 uppercase">Propietario: {{ col.owner_name || dossier.customer.full_name }}</p>
                </div>
                <div class="text-right">
                  <p class="text-[9px] font-bold text-slate-400 uppercase">Valor Avalúo</p>
                  <p class="text-xl font-mono font-black text-emerald-600">${{ formatNum(col.guarantee_amount) }}</p>
                  <div class="mt-2 text-[8px] font-black px-1.5 py-0.5 bg-brand-600 text-white rounded">COBERTURA: {{ col.coverage_percentage }}%</div>
                </div>
              </div>

              <!-- VISOR DE FOTOS DE GARANTÍA -->
              <div class="grid grid-cols-2 gap-2 mb-4">
                <div v-if="col.photo_url_1" class="h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                   <img :src="col.photo_url_1" class="w-full h-full object-cover" />
                </div>
                <div v-if="col.photo_url_2" class="h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                   <img :src="col.photo_url_2" class="w-full h-full object-cover" />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4 text-[10px] mb-4">
                <div class="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <span class="text-slate-400 block uppercase font-bold">Registro</span>
                  <span class="font-bold">{{ col.registration_number || 'N/A' }} ({{ col.registration_date || 'Sin fecha' }})</span>
                </div>
                <div class="p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <span class="text-slate-400 block uppercase font-bold">Estado Legal</span>
                  <span class="font-bold text-emerald-600">{{ col.status }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'disbursement'" class="animate-fade-in max-w-2xl mx-auto py-6">
            <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-[2.5rem] shadow-2xl overflow-hidden">
              <div class="bg-slate-900 p-8 text-center">
                <Wallet class="w-12 h-12 text-white mx-auto mb-4" />
                <h3 class="text-xl font-black text-white uppercase tracking-widest">Ejecución de Desembolso</h3>
                <p class="text-[10px] text-slate-400 mt-2 uppercase tracking-[0.2em]">Cajero/Autorizador: {{ approvingUser }}</p>
              </div>
              <div class="p-10 space-y-6">

                <div class="space-y-2 animate-fade-in">
                  <label :class="labelClasses">Medio de Pago</label>
                  <select v-model="form.disbursement_type" required :class="inputClasses" class="dark:bg-slate-950">
                    <option value="" disabled>Seleccione el método de entrega...</option>
                    <option value="EFECTIVO">Efectivo en Caja</option>
                    <option value="TRANSFERENCIA">Transferencia Bancaria</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>

                <div class="space-y-2 animate-fade-in">
                  <label :class="labelClasses">Notas o Referencias (No. Cheque, Transacción, etc.)</label>
                  <textarea v-model="form.disbursement_notes" rows="2" :class="inputClasses" class="dark:bg-slate-950" placeholder="Escriba detalles del comprobante..."></textarea>
                </div>

                <!-- SECCIÓN NUEVA: CONSOLIDACIÓN Y REFINANCIAMIENTO -->
                <div class="pt-6 mt-6 border-t border-slate-200 dark:border-slate-700 animate-fade-in">
                  <h4 class="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4 flex items-center gap-2">
                    <ArrowRightLeft class="w-4 h-4 text-brand-600" />
                    Opciones de Consolidación
                  </h4>

                  <!-- Refinanciamiento Interno -->
                  <div class="mb-5">
                    <label class="flex items-center gap-3 cursor-pointer select-none">
                      <input type="checkbox" v-model="form.consolidate_internal" class="w-4 h-4 text-brand-600 rounded border-slate-300 focus:ring-brand-500">
                      <span class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Cancelar crédito anterior (Refinanciamiento)</span>
                    </label>
                  </div>

                  <div v-if="form.consolidate_internal" class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-5 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-700">
                    <div>
                      <label :class="labelClasses">Seleccionar Crédito Activo</label>
                      <select v-model="form.internal_loan_id" :class="inputClasses" class="py-2.5">
                        <option value="">-- Seleccione el Crédito --</option>
                        <option v-for="l in activeCustomerLoans" :key="l.id" :value="l.id">
                          {{ l.code }} - Saldo: ${{ formatNum(l.balance || l.amount) }}
                        </option>
                      </select>
                    </div>
                    <div>
                      <label :class="labelClasses">Monto a Retener / Cancelar</label>
                      <input type="number" v-model="form.internal_loan_balance" :class="inputClasses" class="py-2.5 font-mono" placeholder="0.00" />
                    </div>
                  </div>

                  <!-- Consolidación Externa -->
                  <div class="mb-3 flex justify-between items-center">
                    <label class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Cheques a Otras Entidades (Externa)</label>
                    <button type="button" @click="addExternalCheque" class="text-[10px] bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400 px-3 py-1.5 rounded-lg font-bold uppercase hover:bg-brand-200 dark:hover:bg-brand-900 transition-colors flex items-center gap-1">
                      <Plus class="w-3 h-3" /> Agregar Cheque
                    </button>
                  </div>

                  <div v-if="form.external_cheques.length > 0" class="space-y-3 mb-6">
                    <div v-for="(chk, i) in form.external_cheques" :key="i" class="flex gap-3 items-center p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-700">
                      <div class="flex-1">
                        <label :class="labelClasses">Entidad a Pagar</label>
                        <input type="text" v-model="chk.entity" :class="inputClasses" class="py-2" placeholder="Ej. Banco Ficohsa" />
                      </div>
                      <div class="flex-1">
                        <label :class="labelClasses">Nº Cheque</label>
                        <input type="text" v-model="chk.reference" :class="inputClasses" class="py-2" placeholder="0012345" />
                      </div>
                      <div class="w-1/3">
                        <label :class="labelClasses">Monto</label>
                        <input type="number" v-model="chk.amount" :class="inputClasses" class="py-2 font-mono" placeholder="0.00" />
                      </div>
                      <div class="pt-5">
                        <button type="button" @click="removeExternalCheque(i)" class="p-2 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-xl hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors">
                          <Trash2 class="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- RESUMEN FINAL DEL DESEMBOLSO -->
                  <div class="mt-6 p-5 bg-slate-50  dark:bg-slate-900/50 rounded-2xl border border-brand-200 dark:border-brand-900/30">
                    <h4 class="text-[10px] font-black text-brand-700 dark:text-brand-400 uppercase tracking-widest mb-4">Resumen de Liquidación</h4>
                    <div class="space-y-2 text-sm font-mono dark:text-slate-300">
                      <div class="flex justify-between items-center font-bold">
                        <span>Monto Aprobado del Préstamo:</span>
                        <span>${{ formatNum(dossier?.loan?.amount) }}</span>
                      </div>
                      <div v-if="form.consolidate_internal" class="flex justify-between items-center text-red-600 dark:text-red-400 text-xs">
                        <span>(-) Retención Crédito Interno:</span>
                        <span>-${{ formatNum(form.internal_loan_balance) }}</span>
                      </div>
                      <div v-if="form.external_cheques.length > 0" class="flex justify-between items-center text-red-600 dark:text-red-400 text-xs">
                        <span>(-) Retención Cheques Externos ({{ form.external_cheques.length }}):</span>
                        <span>-${{ formatNum(externalTotalAmount) }}</span>
                      </div>
                      <div class="flex justify-between items-center font-black text-lg text-emerald-600 dark:text-emerald-400 pt-3 border-t border-brand-200 dark:border-slate-700 mt-3">
                        <span>NETO A ENTREGAR AL CLIENTE:</span>
                        <span>${{ formatNum(netDisbursementAmount) }}</span>
                      </div>
                    </div>
                  </div>

                </div>

              </div>
            </div>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <div class="flex items-center gap-3">
          <p class="text-[9px] text-slate-400 font-mono font-bold uppercase tracking-widest"></p>
        </div>
        <div class="flex gap-3">
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
          <button @click="handleRever" type="button" :class="btnSecondary">Revertir</button>
          <button v-if="activeTab !== 'disbursement'" @click="activeTab = 'disbursement'" type="button" :class="btnPrimary">Ir a Desembolsar</button>
          <button v-else type="submit" form="disbursementForm" :disabled="isSaving || !form.disbursement_type" :class="btnPrimary">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Wallet v-else class="w-4 h-4" /> Confirmar Desembolso
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue';
import {
  X, Loader2, ShieldCheck, User, Wallet, Activity, CreditCard, BadgeDollarSign, Plus, Trash2, ArrowRightLeft
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
import { useDisbursementVoucher } from '@/composables/reports/useDisbursementVoucher';
import Swal from 'sweetalert2';
const { generateVoucher } = useDisbursementVoucher();

const props = defineProps({ show: Boolean, loanId: String });
const emit = defineEmits(['close', 'resolved']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);
const isLoadingDossier = ref(false);
const dossier = ref(null);
const activeTab = ref('customer');
const globalCatalog = ref([]);

// VARIABLES PARA CONSOLIDACIÓN Y LIQUIDACIÓN
const activeCustomerLoans = ref([]);
const isLoadingPlan = ref(false);

const approvingUser = computed(() => SecureStorage.getItem('llevo_user')?.full_name || 'ADMINISTRADOR');

const dataClasses = "text-sm font-bold text-slate-900 dark:text-white uppercase";
const inputClasses = "w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all dark:text-white";
const labelClasses = "text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase mb-1 block tracking-widest";
const sectionCardClasses = "bg-slate-50/50 dark:bg-slate-800/40 p-6 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm";
const sectionTitleClasses = "text-xs font-black text-slate-900 dark:text-white flex items-center gap-2 mb-6 uppercase tracking-tight";
const btnPrimary = "px-8 py-3 text-[10px] font-black text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 uppercase tracking-widest";
const btnSecondary = "px-6 py-3 text-[10px] font-black text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors uppercase tracking-widest";
const loanData = ref({});
const tabs = [
  { id: 'customer', name: 'Perfil Cliente' },
  { id: 'credit', name: 'Crédito' },
  { id: 'payments', name: 'Plan Pagos' },
  { id: 'balance', name: 'Análisis Flujo' },
  { id: 'collaterals', name: 'Garantías' },
  { id: 'disbursement', name: 'Desembolso' },
];

const daysMap = [
  { key: 'monday', label: 'Lun', labelFull: 'Lunes' }, { key: 'tuesday', label: 'Mar', labelFull: 'Martes' }, { key: 'wednesday', label: 'Mié', labelFull: 'Miércoles' },
  { key: 'thursday', label: 'Jue', labelFull: 'Jueves' }, { key: 'friday', label: 'Vie', labelFull: 'Viernes' }, { key: 'saturday', label: 'Sáb', labelFull: 'Sábado' },
  { key: 'sunday', label: 'Dom', labelFull: 'Domingo' }
];

const form = reactive({
  disbursement_type: '',
  disbursement_notes: '',
  consolidate_internal: false,
  internal_loan_id: '',
  internal_loan_balance: 0,
  external_cheques: []
});

const formatNum = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseJSON = (str) => { try { return typeof str === 'string' ? JSON.parse(str) : (str || []); } catch { return []; } };

const calculateAge = (date) => {
  if(!date) return 'N/A';
  const birth = new Date(date);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) age--;
  return age;
};

const getGlobalText = (value, type) => {
    const item = globalCatalog.value.find(g => Number(g.type) === type && String(g.value) === String(value));
    return item ? item.text : (value || 'N/D');
};

const getDayStatusClass = (day, evalData) => {
  const good = parseJSON(evalData.good_days_json);
  const regular = parseJSON(evalData.regular_days_json);
  if (good.includes(day)) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (regular.includes(day)) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
};

const getDayShortLabel = (day, evalData) => {
  const good = parseJSON(evalData.good_days_json);
  const regular = parseJSON(evalData.regular_days_json);
  if (good.includes(day)) return 'Bueno';
  if (regular.includes(day)) return 'Regular';
  return 'Malo';
};

const calculateDSCR = () => {
  if (!dossier.value?.evaluation) return "0.00";
  const monthlyNet = Number(dossier.value.evaluation.final_daily_available || 0) * 30;
  const quota = Number(dossier.value.loan?.estimated_quota || 1);
  return (monthlyNet / quota).toFixed(2);
};

const capacityColor = computed(() => {
  const ratio = Number(calculateDSCR());
  if (ratio >= 1.5) return { text: 'text-emerald-500' };
  if (ratio >= 1.0) return { text: 'text-amber-500' };
  return { text: 'text-red-500' };
});

const addExternalCheque = () => {
  form.external_cheques.push({ entity: '', reference: '', amount: 0 });
};

const removeExternalCheque = (index) => {
  form.external_cheques.splice(index, 1);
};

const externalTotalAmount = computed(() => {
  return form.external_cheques.reduce((sum, chk) => sum + Number(chk.amount || 0), 0);
});

const netDisbursementAmount = computed(() => {
  if (!dossier.value) return 0;
  let total = Number(dossier.value.loan.amount);
  if (form.consolidate_internal) total -= Number(form.internal_loan_balance || 0);
  total -= externalTotalAmount.value;
  return total > 0 ? total : 0;
});

// ============================================================================
// LÓGICA DE LIQUIDACIÓN TÉCNICA
// ============================================================================
const daysElapsed = (loan) => {
  const lastDateStr = loan.last_payment_date || loan.disbursed_at || loan.created_at;
  if (!lastDateStr) return 0;
  const lastDate = new Date(lastDateStr);
  const today = new Date();
  lastDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffTime = Math.abs(today - lastDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const calculateAccruedInterest = (loan, plan) => {
  if (!loan || !plan) return 0;
  const annualRate = Number(loan.rate || 0);
  const dailyRate = (annualRate / 100) / 365;
  const principal = Number(loan.remaining_balance || loan.amount || 0);
  return principal * dailyRate * daysElapsed(loan);
};

const calculateTotalPayoff = (loan, plans) => {
  const principal = Number(loan.remaining_balance || loan.amount || 0);
  if (loan.interest_type === 'FLAT') {
    const remainingInterest = plans.reduce((sum, q) => sum + Number(q.interest || 0), 0);
    return principal + remainingInterest;
  } else {
    const nextPlan = plans[0];
    return principal + calculateAccruedInterest(loan, nextPlan);
  }
};

watch(() => form.internal_loan_id, async (newId) => {
  if (newId) {
    const selected = activeCustomerLoans.value.find(l => l.id === newId);
    if (selected) {
      isLoadingPlan.value = true;
      try {
        const res = await apiClient.get(`/payment_plans?loan_id=${newId}&status=PENDING`);
        const plans = Array.isArray(res.data) ? res.data : (res.data.data || []);
        form.internal_loan_balance = calculateTotalPayoff(selected, plans).toFixed(2);
      } catch (e) {
        showError("No se pudo calcular la liquidación del crédito.");
        form.internal_loan_balance = Number(selected.balance || selected.amount || 0);
      } finally {
        isLoadingPlan.value = false;
      }
    }
  } else {
    form.internal_loan_balance = 0;
  }
});

// ============================================================================

watch(() => props.show, async (val) => {
  if (val && props.loanId) {
    activeTab.value = 'customer';
    form.disbursement_type = '';
    form.disbursement_notes = '';
    form.consolidate_internal = false;
    form.internal_loan_id = '';
    form.internal_loan_balance = 0;
    form.external_cheques = [];

    dossier.value = null;
    isLoadingDossier.value = true;

    try {
      const user = SecureStorage.getItem('llevo_user');

      const [loanRes, globalsRes] = await Promise.all([
        apiClient.get(`/loans/${props.loanId}`),
        apiClient.get(`/globals`)
      ]);

      globalCatalog.value = globalsRes.data.data || globalsRes.data;

      const currentLoan = Array.isArray(loanRes.data) ? loanRes.data[0] : (loanRes.data.data || loanRes.data);
      loanData.value = currentLoan;

      if (!loanData.value?.customer_id || !loanData.value?.product_id) {
        throw new Error("Datos de origen del préstamo incompletos (ID cliente/producto).");
      }

      const [customerRes, evaluationRes, collateralsRes, planRes, productRes, activeLoansRes] = await Promise.all([
        apiClient.get(`/customers/${loanData.value.customer_id}`),
        apiClient.get(`/financial_evaluations?customer_id=${loanData.value.customer_id}`),
        apiClient.get(`/collaterals?loan_id=${props.loanId}&company_id=${user.company}`),
        apiClient.get(`/payment_plans?loan_id=${props.loanId}`).catch(() => ({ data: [] })),
        apiClient.get(`/credit_products/${loanData.value.product_id}`),
        apiClient.get(`/loans?customer_id=${loanData.value.customer_id}&status=ACTIVE&company_id=${user.company}`).catch(() => ({ data: [] }))
      ]);

      const custRaw = customerRes.data?.data || customerRes.data;
      const evalData = Array.isArray(evaluationRes.data) ? evaluationRes.data[0] : (evaluationRes.data?.data || evaluationRes.data);
      const collatData = collateralsRes.data?.data || collateralsRes.data || [];
      const planData = planRes.data?.data || planRes.data || [];
      const prodData = productRes.data?.data || productRes.data;

      const activeLoansRaw = activeLoansRes.data?.data || activeLoansRes.data || [];
      activeCustomerLoans.value = activeLoansRaw.filter(l => l.id !== props.loanId);

      const needsGuaranty = prodData.guaranty_type && prodData.guaranty_type !== 'NONE';

      if (!evalData || (needsGuaranty && !collatData.length)) {
        const msg = !evalData ? "Falta Evaluación Financiera." : "Faltan Garantías Obligatorias para este tipo de producto.";
        showError(msg);
        emit('close');
        return;
      }

      dossier.value = {
        loan: {
          ...loanData.value,
          estimated_quota: Number(loanData.value.total_amount || 0) / Number(loanData.value.term || 1)
        },
        customer: {
          ...custRaw,
          full_name: `${custRaw.first_name || ''} ${custRaw.middle_name || ''} ${custRaw.last_name || ''} ${custRaw.second_last_name || ''}`.replace(/\s+/g, ' ').trim()
        },
        evaluation: evalData,
        collaterals: collatData,
        payment_plans: planData,
        product: prodData
      };
    } catch (e) {
      console.error("Error cargando desembolso:", e);
      showError('Error al recuperar expediente para desembolso.');
      emit('close');
    } finally {
      isLoadingDossier.value = false;
    }
  }
});

const handleRever = async () => {
  // 1. Validación de seguridad final antes de procesar
  //console.log(props.loanId)
  const result = await Swal.fire({
    title: '¿Confirmar Revertir a Solicitud?',
    text: "Esta acción revertirá el crédito a su estado inicial y no se puede deshacer.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#2563eb', // Color azul/brand
    cancelButtonColor: '#ef4444', // Color rojo
    confirmButtonText: 'Sí, revertir',
    cancelButtonText: 'Cancelar'
  });

  if (!result.isConfirmed) {
    return; // Si le da a cancelar o cierra el modal, cortamos todo aquí
  }
  form.status='PENDING'
  isSaving.value = true;
  try {
    const payload = {
      status: form.status,
      committee_notes: form.committee_notes,
      approved_by: approvingUser.value
    };

    // Llamada al endpoint de actualización de préstamo para el dictamen
    await apiClient.put(`/loans/${props.loanId}`, payload);
    showSuccess('Credito reenviado a solicitúd.');
    //handlePrintSummary();
    emit('resolved');
    emit('close');
  } catch (e) {
    console.error(e);
    showError('Error al guardar el veredicto. Intente de nuevo.');
  } finally {
    isSaving.value = false;
  }
};

const handleSubmit = async () => {
  if (!form.disbursement_type) return showError('Seleccione el medio de pago.');
  if (form.consolidate_internal && !form.internal_loan_id) return showError('Seleccione el crédito interno a cancelar.');

  const result = await Swal.fire({
    title: '¿Confirmar Desembolso?',
    text: "Esta acción afectará la caja y no se puede deshacer.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#2563eb', // Color azul/brand
    cancelButtonColor: '#ef4444', // Color rojo
    confirmButtonText: 'Sí, desembolsar',
    cancelButtonText: 'Cancelar'
  });

  if (!result.isConfirmed) {
    return; // Si le da a cancelar o cierra el modal, cortamos todo aquí
  }

  isSaving.value = true;
  const user = SecureStorage.getItem('llevo_user');

  // Replicamos el formato de fecha exacto que usa la Caja
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const mysqlDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  try {
    // 1. CANCELAR EL CRÉDITO VIEJO EN CAJA (Si es refinanciamiento interno)
    if (form.consolidate_internal && form.internal_loan_id) {
       const oldLoan = activeCustomerLoans.value.find(l => l.id === form.internal_loan_id);

       // Obtenemos la cuota pendiente para extraer los días calculados (Igual que en caja)
       let oldNextInstallment = null;
       try {
         const resPlan = await apiClient.get(`/payment_plans?loan_id=${form.internal_loan_id}&status=PENDING`);
         const plans = Array.isArray(resPlan.data) ? resPlan.data : (resPlan.data.data || []);
         if (plans.length > 0) oldNextInstallment = plans[0];
       } catch(e) {}

       const amountPaid = Number(form.internal_loan_balance);
       const principal = Number(oldLoan.remaining_balance || oldLoan.amount || 0);
       let interest = amountPaid - principal;
       if (interest < 0) interest = 0; // Seguridad matemática

       // Enviamos el pago simulando EXACTAMENTE a la caja
       await apiClient.post('/payments', {
        loan_id: form.internal_loan_id,
        company_id: user.company,
        amount_paid: amountPaid,
        principal_applied: principal,
        interest_applied: Number(interest.toFixed(2)),
        late_fee_applied: 0,
        payment_method: 'TRANSFERENCIA',
        notes: `LIQUIDACIÓN POR REFINANCIAMIENTO (Crédito ${loanData.value.code})`,
        payment_date: mysqlDate,
        days_calculated: oldNextInstallment ? Number(oldNextInstallment.days) : daysElapsed(oldLoan),
        cashier_id: user.id
      });
    }

    // 2. ACTUALIZAR EL PRÉSTAMO PRINCIPAL
    const payload = {
      status: 'ACTIVE',
      disbursement_method: form.disbursement_type,
      disbursement_notes: form.disbursement_notes,
      approved_by: approvingUser.value,
      consolidate_internal: form.consolidate_internal ? 1 : 0,
      internal_loan_id: form.internal_loan_id || null,
      internal_loan_balance: form.internal_loan_balance || 0,
      net_disbursed_amount: netDisbursementAmount.value
    };

    await apiClient.put(`/loans/${props.loanId}`, payload);

    // 3. REGISTRAR LOS CHEQUES EXTERNOS (Si hay)
    if (form.external_cheques.length > 0) {
      const chequePromises = form.external_cheques.map(chk => {
        return apiClient.post('/loan_external_cheques', {
          loan_id: props.loanId,
          entity: chk.entity,
          reference: chk.reference,
          amount: chk.amount
        });
      });
      await Promise.all(chequePromises);
    }

    // 4. CREAR LA TRANSACCIÓN DE DESEMBOLSO DEL NUEVO CRÉDITO
    // 4. CREAR LA TRANSACCIÓN DE DESEMBOLSO Y EL ASIENTO CONTABLE
    const transactionPayload = {
      loan_id: props.loanId,
      loan_code: dossier.value.loan.code, // <-- Agregado para el asiento contable
      customer_id: dossier.value.loan.customer_id, // <-- Agregado para el asiento contable
      company_id: user.company,
      transaction_type: 'DISBURSEMENT',
      total_amount: netDisbursementAmount.value,
      principal_applied: dossier.value.loan.amount, // El asiento se hace por el total del capital
      interest_applied: 0,
      balance_after: dossier.value.loan.amount,
      transaction_date: mysqlDate
    };

    // Cambiamos a la ruta especializada en lugar del CRUD genérico
    await apiClient.post(`/loan_transactions/disburse`, transactionPayload);

    showSuccess('Crédito desembolsado y consolidado exitosamente.');
    emit('resolved');
    emit('close');
    handlePrint();
  } catch (e) {
    console.error(e);
    showError('Error al procesar desembolso y liquidación.');
  } finally {
    isSaving.value = false;
  }
};

const handlePrint = () => {
  if(dossier.value){
    dossier.value.loan.disbursement_method=form.disbursement_type;
    dossier.value.loan.disbursement_notes=form.disbursement_notes;
    dossier.value.loan.net_disbursed = netDisbursementAmount.value;
    dossier.value.loan.internal_retention = form.consolidate_internal ? form.internal_loan_balance : 0;
    dossier.value.loan.external_retention = externalTotalAmount.value;

    generateVoucher(
      dossier.value
    );
  }
};
</script>